import {
  QueryKey,
  UndefinedInitialDataInfiniteOptions,
  useInfiniteQuery,
} from '@tanstack/react-query';
import { SessionHistoryEntry } from 'common/components/SessionHistory/services/SessionHistoryApi.ts';
import { ErrorCodes } from 'common/responseCodes.ts';
import { subMilliseconds } from 'date-fns';
import { useEffect, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';

type UseSessionHistoryInfiniteQueryProps = {
  queryKey: QueryKey;
  queryFn: UndefinedInitialDataInfiniteOptions<SessionHistoryEntry[]>['queryFn'];
  enabled?: boolean;
};

export const SESSION_HISTORY_PAGE_SIZE = 20;

export const getSessionHistoryNextPageParam = (lastPage: SessionHistoryEntry[]) => {
  if (lastPage.length < SESSION_HISTORY_PAGE_SIZE) {
    return;
  }

  const effectiveStart = lastPage.at(-1)!.effectiveStart;

  if (!effectiveStart) {
    return;
  }

  return subMilliseconds(effectiveStart, 1);
};

export const useSessionHistoryInfiniteQuery = ({
  queryKey,
  queryFn,
  enabled = true,
}: UseSessionHistoryInfiniteQueryProps) => {
  const {
    data: sessionHistoryPages,
    fetchNextPage,
    isFetchingNextPage,
    isFetched,
    isLoading,
    hasNextPage,
  } = useInfiniteQuery<SessionHistoryEntry[]>({
    queryKey: queryKey,
    queryFn: queryFn,
    initialPageParam: new Date(),
    enabled: enabled,
    retry: (failureCount, error) => {
      if (error instanceof Error && error.message === ErrorCodes.NotFound) {
        return false;
      }
      return failureCount < 3;
    },
    getNextPageParam: getSessionHistoryNextPageParam,
  });

  const sessionHistory: SessionHistoryEntry[] = useMemo(
    () =>
      sessionHistoryPages?.pages.reduce((history, page) => {
        return [...history, ...page];
      }, []) || [],
    [sessionHistoryPages],
  );

  const { ref, inView } = useInView({
    threshold: 0,
  });

  useEffect(() => {
    if (inView && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, isFetchingNextPage]);

  return {
    refToLoader: ref,
    showLoader: hasNextPage,
    sessionHistory,
    fetchingEnded: isFetched,
    isLoading: isLoading,
  };
};
